import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { MouseEvent } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';

import { RightContext } from '@/components/RightProvider/context';
import {
  aiRemovalURL,
  faceSwapperURL,
  videoTranslateURL,
  viralTemplateURL,
} from '@/utils/routeConstants';

import { MutedIcon, UnmutedIcon } from '../../components/common/VideoCarousel';
import { type HeroVideoItem, type TemplateVideoItem } from './config';
import styles from './index.module.scss';

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduce(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  return reduce;
}

function useInViewPlayback<T extends HTMLElement>(rootMargin = '120px') {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
      },
      { rootMargin, threshold: 0.2 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin]);

  return { ref, inView };
}

export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.classList.add(styles.visible);
          observer.unobserve(node);
        }
      },
      { threshold: 0.16 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return ref;
}

export const ArrowIcon = ({ color = 'white' }: { color?: string }) => (
  <span aria-hidden="true">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        d="M3.33337 8H12.6667"
        stroke={color}
        strokeWidth="1.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 3.3335L12.6667 8.00016L8 12.6668"
        stroke={color}
        strokeWidth="1.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const TrendIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
  >
    <g clipPath="url(#clip0_2014_9665)">
      <path
        d="M11 3.5L6.75 7.75L4.25 5.25L1 8.5"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 3.5H11V6.5"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_2014_9665">
        <rect width="12" height="12" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const SoundIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.4575 5.70557L9.07104 8.09206"
      stroke="#333333"
      strokeWidth="1.125"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.07104 5.70557L11.4575 8.09206"
      stroke="#333333"
      strokeWidth="1.125"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.75024 1.6875V11.8125C4.78149 11.8125 3.31857 9.236 3.31857 9.236H1.68774C1.37708 9.236 1.12524 8.98417 1.12524 8.6735V4.78429C1.12524 4.47362 1.37708 4.22179 1.68774 4.22179H3.31857C3.31857 4.22179 4.78149 1.6875 6.75024 1.6875Z"
      stroke="#333333"
      strokeWidth="1.125"
      strokeLinejoin="round"
    />
  </svg>
);

const HeroMediaPoster = ({
  poster,
  isHidden = false,
}: {
  poster: string;
  isHidden?: boolean;
}) => {
  const posterClassName = `${styles.heroVideoPoster} ${
    isHidden ? styles.heroVideoPosterHidden : ''
  }`;
  const fallbackClassName = `${styles.heroVideoPosterFallback} ${
    isHidden ? styles.heroVideoPosterHidden : ''
  }`;

  return poster ? (
    <div
      className={posterClassName}
      style={{ backgroundImage: `url(${JSON.stringify(poster)})` }}
      aria-hidden
    />
  ) : (
    <div className={fallbackClassName} aria-hidden />
  );
};

export const HeroMarqueeVideoCard = ({
  item,
  slotKey,
  isUnmuted,
  onToggleSound,
  compact = false,
  isActive = true,
  /** 为 true 时不过度依赖 IO（避免父级 overflow:hidden 等导致永远不 inView） */
  eagerVideo = false,
}: {
  item: HeroVideoItem;
  slotKey: string;
  isUnmuted: boolean;
  onToggleSound: () => void;
  compact?: boolean;
  isActive?: boolean;
  eagerVideo?: boolean;
}) => {
  const reduceMotion = usePrefersReducedMotion();
  /** 收窄 rootMargin，减少「同时认为在视口内」的卡片数量，降低并发拉流 */
  const { ref: rootRef, inView } = useInViewPlayback<HTMLDivElement>('24px 64px');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoReadyRef = useRef(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  const poster = item.poster.trim();
  const videoUrl = item.videoUrl?.trim() ?? '';
  const showVideo = videoUrl.length > 0 && !reduceMotion && !videoFailed;
  const shouldPlayVideo = isActive && (eagerVideo || inView);
  const mountVideo = showVideo && (shouldPlayVideo || videoReady);
  const iconUid = slotKey.replace(/[^a-zA-Z0-9_-]/g, '') || 'slot';

  useEffect(() => {
    videoReadyRef.current = false;
    setVideoReady(false);
    setVideoFailed(false);
  }, [videoUrl]);

  const handleVideoReady = () => {
    videoReadyRef.current = true;
    setVideoReady(true);
  };

  useEffect(() => {
    if (!mountVideo) return;
    const el = videoRef.current;
    if (!el) return;
    el.muted = !isUnmuted;
    if (shouldPlayVideo) {
      el.play().catch(() => setVideoFailed(true));
      return;
    }
    el.pause();
  }, [mountVideo, shouldPlayVideo, isUnmuted, videoUrl]);

  useEffect(() => {
    return () => {
      const el = videoRef.current;
      if (!el || videoReadyRef.current) return;
      el.pause();
      el.removeAttribute('src');
      el.load();
    };
  }, [videoUrl]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !mountVideo || !shouldPlayVideo) return;
    el.muted = !isUnmuted;
    if (!el.muted) {
      el.play().catch(() => {});
    }
  }, [isUnmuted, mountVideo, shouldPlayVideo]);

  return (
    <div
      ref={rootRef}
      className={compact ? styles.heroMediaCompact : styles.heroMediaCard}
    >
      {showVideo ? (
        mountVideo ? (
          <>
            <video
              ref={videoRef}
              src={videoUrl}
              className={videoReady ? styles.heroVideoReady : styles.heroVideoLoading}
              loop
              playsInline
              muted={!isUnmuted}
              preload="metadata"
              onLoadedData={handleVideoReady}
              onCanPlay={handleVideoReady}
              onError={() => setVideoFailed(true)}
            />
            <HeroMediaPoster poster={poster} isHidden={videoReady} />
          </>
        ) : poster ? (
          <HeroMediaPoster poster={poster} />
        ) : (
          <div className={styles.heroVideoPosterFallback} aria-hidden />
        )
      ) : poster ? (
        <HeroMediaPoster poster={poster} />
      ) : null}
      {mountVideo && isActive ? (
        <button
          className={styles.soundButton}
          type="button"
          aria-label={isUnmuted ? 'Mute' : 'Unmute'}
          onClick={(e) => {
            e.stopPropagation();
            onToggleSound();
          }}
        >
          {isUnmuted ? <UnmutedIcon /> : <MutedIcon uniqueId={iconUid} />}
        </button>
      ) : null}
    </div>
  );
};

export const HeroAIToolsStrip = () => {
  const tools = [
    {
      href: videoTranslateURL,
      title: 'AI Translate',
      image: '/images/3.0-ai-translate.png',
    },
    {
      href: aiRemovalURL,
      title: 'AI Removal',
      image: '/images/3.0-ai-removal.png',
    },
    {
      href: faceSwapperURL,
      title: 'AI Face Swap',
      image: '/images/3.0-faceSwap.png',
    },
  ];

  return (
    <div className={styles.heroToolsInner}>
      {tools.map((tool) => (
        <Link
          key={tool.href}
          href={tool.href}
          className={styles.heroToolCard}
          aria-label={tool.title}
        >
          <div className={styles.heroToolImageOnly}>
            <Image
              src={tool.image}
              alt=""
              fill
              sizes="404px"
              className={`object-cover ${styles.heroToolImage}`}
              priority={false}
            />
          </div>
          <div className={styles.heroToolOverlay} aria-hidden />
          <div className={styles.heroToolCaption} aria-hidden>
            <p className={styles.heroToolCaptionTitle}>{tool.title}</p>
            <span className={styles.heroToolCaptionBtn}>
              <ArrowIcon color="#fff" />
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
};

export const MediaCard = ({
  item,
  compact = false,
}: {
  item: HeroVideoItem;
  compact?: boolean;
}) => {
  const [videoReady, setVideoReady] = useState(false);
  const videoUrl = item.videoUrl?.trim() ?? '';
  const poster = item.poster.trim();

  useEffect(() => {
    setVideoReady(false);
  }, [videoUrl]);

  return (
    <div className={compact ? styles.heroMediaCompact : styles.heroMediaCard}>
      {videoUrl ? (
        <>
          <video
            src={videoUrl}
            className={videoReady ? styles.heroVideoReady : styles.heroVideoLoading}
            muted
            loop
            playsInline
            preload="none"
            onLoadedData={() => setVideoReady(true)}
            onCanPlay={() => setVideoReady(true)}
          />
          <HeroMediaPoster poster={poster} isHidden={videoReady} />
        </>
      ) : (
        <HeroMediaPoster poster={poster} />
      )}
      {/* <span className={styles.mediaBadge}>{item.label}</span> */}
      <button className={styles.soundButton} type="button" aria-label="Toggle sound">
        <SoundIcon />
      </button>
    </div>
  );
};

export const TemplateCard = ({ item }: { item: TemplateVideoItem }) => {
  const router = useRouter();
  const rightContext = useContext(RightContext);
  const reduceMotion = usePrefersReducedMotion();
  const { ref: articleRef, inView } = useInViewPlayback<HTMLAnchorElement>();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoFailed, setVideoFailed] = useState(false);

  const poster = item.poster.trim();
  const videoUrl = item.videoUrl?.trim() ?? '';
  const showVideo = videoUrl.length > 0 && !reduceMotion && !videoFailed;
  const mountVideo = showVideo && inView;
  const templateHref = `${viralTemplateURL}/${encodeURIComponent(
    item.id,
  )}?subtab=trending&from=home`;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !showVideo) return;
    if (inView) {
      const run = el.play();
      if (run !== undefined) {
        run.catch(() => setVideoFailed(true));
      }
    } else {
      el.pause();
    }
  }, [inView, showVideo, videoUrl]);

  const handleTemplateClick = async (event: MouseEvent<HTMLAnchorElement>) => {
    if (rightContext.getIsLoggedIn()) return;

    event.preventDefault();
    await rightContext.getUserLoginState({
      onReady: () => {
        void router.push(templateHref);
      },
    });
  };

  return (
    <Link
      ref={articleRef}
      href={templateHref}
      className={styles.templateCard}
      aria-label={item.title}
      onClick={handleTemplateClick}
    >
      {mountVideo ? (
        <video
          ref={videoRef}
          src={videoUrl}
          poster={poster}
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden
          onError={() => setVideoFailed(true)}
        />
      ) : (
        <img src={poster} alt="" loading="lazy" decoding="async" />
      )}
      <div className={styles.templateOverlay} />
      <span className={styles.categoryBadge}>{item.category}</span>
      {item.trend ? (
        <span className={styles.trendBadge}>
          <TrendIcon />
          {item.trend}
        </span>
      ) : null}
      <div className={styles.templateCaption}>
        {/* <p>{item.title}</p> */}
        <p></p>
        <span className={styles.templateCardAction}>
          <ArrowIcon />
        </span>
      </div>
    </Link>
  );
};

export const RevealSection = ({
  children,
  className = '',
  id,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => {
  const ref = useReveal<HTMLElement>();
  return (
    <section ref={ref} id={id} className={`${styles.reveal} ${className}`}>
      {children}
    </section>
  );
};
